import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { attachScrollTriggerOrPlayNow } from "../utils/scrollTriggerHelpers";

gsap.registerPlugin(ScrollTrigger);

/**
 * Hero Small block animation.
 *
 * Variante "compact" del blocco hero per pagine interne (420px fissi).
 * Trigger: ScrollTrigger one-shot all'entrata in viewport (top 85%).
 *
 * Sequenza:
 *   T=0.0  Bg: fade-in 0.6s (power2.out)
 *   T=0.0  Bg: grayscale 1→0 in 3s (power2.inOut) — più rapido dell'hero (era 5s)
 *   T=0.0  Bg: contrast 0.95→1.02 in 3s
 *   T=0.0  Bg: zoom 1.05→1 in 4s (lineare, drift impercettibile)
 *   T=0.4  Titolo: fade + slide-up + blur clearing (1.2s)
 *   T=0.7  Testo (opzionale): fade + slide-up + blur (1.0s)
 *
 * Pattern coerente con hero homepage ma più rapido (banner secondario).
 */

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initHeroSmall = (block) => {
  const bg = block.querySelector("[data-hero-small-bg]");
  const bgImg = bg ? bg.querySelector("img") : null;
  const title = block.querySelector("[data-hero-small-title]");
  const text = block.querySelector("[data-hero-small-text]");

  if (!bg || !title) return;

  // Reduced motion: niente animazione, tutto visibile subito
  if (prefersReducedMotion()) {
    block.setAttribute("data-hero-small-ready", "true");
    return;
  }

  // Stato iniziale GSAP
  gsap.set(bg, { opacity: 0 });
  gsap.set(title, { opacity: 0, y: 24, filter: "blur(8px)" });
  if (text) gsap.set(text, { opacity: 0, y: 16, filter: "blur(6px)" });

  // Attivo le CSS custom properties per il filtro iniziale (b/n + bassa contrast)
  block.classList.add("is-animatable");

  const tl = gsap.timeline({ paused: true });

  // Bg fade-in
  tl.to(bg, {
    opacity: 1,
    duration: 0.6,
    ease: "power2.out",
  }, 0);

  // Bg: grayscale → colore (CSS custom property pilotata da gsap)
  tl.to(block, {
    "--hero-small-grayscale": 0,
    duration: 3,
    ease: "power2.inOut",
  }, 0);

  // Bg: contrast neutralization
  tl.to(block, {
    "--hero-small-contrast": 1.02,
    duration: 3,
    ease: "power2.inOut",
  }, 0);

  // Bg: zoom-out (1.05 → 1, lineare, drift impercettibile)
  if (bgImg) {
    gsap.set(bgImg, { scale: 1.05 });
    tl.to(bgImg, {
      scale: 1,
      duration: 4,
      ease: "none",
    }, 0);
  }

  // Titolo: fade + slide-up + blur
  tl.to(title, {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    duration: 1.2,
    ease: "power2.out",
  }, 0.4);

  // Testo (opzionale): segue il titolo con un leggero delay
  if (text) {
    tl.to(text, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 1.0,
      ease: "power2.out",
    }, 0.7);
  }

  // Attach ScrollTrigger o play immediato se già in viewport
  attachScrollTriggerOrPlayNow(tl, {
    trigger: block,
    onComplete: () => block.setAttribute("data-hero-small-ready", "true"),
  });
};

const initAllHeroSmall = () => {
  const blocks = document.querySelectorAll("[data-hero-small-block]");
  blocks.forEach(initHeroSmall);
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllHeroSmall);
  } else {
    initAllHeroSmall();
  }
};